import { useState } from 'react';
import { useDataProvider, useListContext, useNotify } from 'react-admin';
import DownloadIcon from '@material-ui/icons/GetApp';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';

import { downloadFile } from 'utils';
import InfoDialog from 'components/InfoDialog';
import {
  ACCOUNTING_REPORT_RESOURCE,
  AccountingReportFile,
  accountingReportErrorMessage,
} from 'providers/accountingReport';
import { API_MAX_ROWS } from 'providers/apiLimits';
import { CircularProgress, Theme, useMediaQuery } from '@material-ui/core';

const MAX_ROWS_LABEL = API_MAX_ROWS.toLocaleString('en-US');
const LABEL = 'Accounting Export';

const AccountingExportButton = ({ total, ...props }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { filterValues } = useListContext();
  // On phones the toolbar cannot wrap, so the label pushes the buttons
  // off-screen; collapse to an icon like the Export button next to it.
  const isXSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs'),
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  // The Pay Period is the pair of date pickers; the report prints it verbatim.
  const startDay = filterValues?.['dateApproved||$gte'];
  const endDay = filterValues?.['dateApproved||$lte'];

  const isMissingPayPeriod = !startDay || !endDay;
  const hasNoRows = total === 0;
  const isDisabled = isLoading || isMissingPayPeriod || hasNoRows;

  const getAccountingExportReport = async () => {
    if (total > API_MAX_ROWS) {
      setIsModalOpen(true);
      return;
    }
    setIsLoading(true);

    try {
      const response = await dataProvider.getOne<AccountingReportFile>(
        ACCOUNTING_REPORT_RESOURCE,
        {
          // @ts-expect-error the export takes `filter` and `payPeriod`, not an id
          filter: filterValues,
          payPeriod: { startDay, endDay },
        },
      );

      const blobURL = URL.createObjectURL(response.data.blob);
      downloadFile(blobURL, `accounting-report-${response.data.id}.xlsx`);
      URL.revokeObjectURL(blobURL);
    } catch (error) {
      // The API answers 400 when the Pay Period is missing or malformed;
      // without this the spinner never stopped and nothing was shown.
      notify(accountingReportErrorMessage(error), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Tooltip
        open={open}
        onClose={handleClose}
        onOpen={
          isMissingPayPeriod || hasNoRows || isXSmall ? handleOpen : undefined
        }
        title={
          isMissingPayPeriod ? (
            <Box>
              <Typography variant="caption">Required filters:</Typography>
              <br />
              <Typography variant="caption">
                &apos;Completed On and Before&apos;
              </Typography>
              <br />
              <Typography variant="caption">
                &apos;Completed On and After&apos;
              </Typography>
            </Box>
          ) : hasNoRows ? (
            <Typography variant="caption">No rows to export</Typography>
          ) : (
            LABEL
          )
        }
      >
        <span>
          {isXSmall ? (
            <IconButton
              color="primary"
              aria-label={LABEL}
              onClick={async () => getAccountingExportReport()}
              disabled={isDisabled}
              {...props}
            >
              {isLoading ? <CircularProgress size={16} /> : <DownloadIcon />}
            </IconButton>
          ) : (
            <Button
              color="primary"
              startIcon={isLoading ? <></> : <DownloadIcon />}
              onClick={async () => getAccountingExportReport()}
              disabled={isDisabled}
              {...props}
            >
              {isLoading ? <CircularProgress size={16} /> : LABEL}
            </Button>
          )}
        </span>
      </Tooltip>
      <InfoDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        title="Attention!"
        content={`Export limit exceeded. You can export a maximum of ${MAX_ROWS_LABEL} rows at a time. Please refine your selection.`}
      />
    </>
  );
};

export default AccountingExportButton;
